import { request } from '../base';
import type { ApiResponse, PaginatedResponse, PaginationParams } from '../base';
import type { ContextaJob, ContextaJobWithContent } from './integrations';

// Auth
export async function login(email: string, password: string) {
  return request<ApiResponse<{ token: string; user: any }>>('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logout() {
  return request<{ message: string }>('/auth/logout', { method: 'POST' });
}

export async function getMe() {
  return request<ApiResponse<any>>('/auth/me');
}

// Passkey Auth
export async function getPasskeyChallenge() {
  return request<{
    challenge: string;
    sessionId: string;
    timeout: number;
    rpId: string;
    allowCredentials: Array<{ type: string; id: string }>;
    userVerification: string;
  }>('/auth/passkey/challenge', { method: 'POST' });
}

export async function verifyPasskey(credential: {
  id: string;
  rawId: string;
  response: {
    clientDataJSON: string;
    authenticatorData: string;
    signature: string;
  };
  type: string;
  sessionId?: string;
}) {
  return request<ApiResponse<{ token: string; user: any }>>('/auth/passkey/verify', {
    method: 'POST',
    body: JSON.stringify(credential),
  });
}

export async function getPasskeyRegisterChallenge() {
  return request<{
    challenge: string;
    sessionId: string;
    rp: { name: string; id: string };
    user: { id: string; name: string; displayName: string };
    pubKeyCredParams: Array<{ alg: number; type: string }>;
    timeout: number;
    attestation: string;
    authenticatorSelection: any;
    excludeCredentials: Array<{ type: string; id: string }>;
  }>('/auth/passkey/register/challenge', { method: 'POST' });
}

export async function completePasskeyRegistration(credential: {
  id: string;
  rawId: string;
  response: {
    clientDataJSON: string;
    attestationObject: string;
  };
  type: string;
  sessionId: string;
  name?: string;
}) {
  return request<ApiResponse<{ id: string; name: string; createdAt: string }>>('/auth/passkey/register/complete', {
    method: 'POST',
    body: JSON.stringify(credential),
  });
}

export async function getPasskeys() {
  return request<{ data: Array<{ id: string; name: string; deviceType: string; createdAt: string; lastUsedAt: string | null }> }>('/auth/passkeys');
}

export async function deletePasskey(id: string) {
  return request<{ message: string }>(`/auth/passkeys/${id}`, { method: 'DELETE' });
}

// Formulas
export async function getFormulas(params?: PaginationParams & { status?: string; type?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.type) searchParams.set('type', params.type);
  return request<PaginatedResponse<any>>(`/formulas?${searchParams}`);
}

export async function getFormula(id: string) {
  return request<ApiResponse<any>>(`/formulas/${id}`);
}

export async function getFormulaVersionsByCode(code: string) {
  return request<ApiResponse<any[]>>(`/formulas/versions/${encodeURIComponent(code)}`);
}

export async function createFormula(data: any) {
  return request<ApiResponse<any>>('/formulas', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateFormula(id: string, data: any) {
  return request<ApiResponse<any>>(`/formulas/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getFormulaBOM(id: string, currency?: string) {
  const params = currency ? `?currency=${currency}` : '';
  return request<ApiResponse<any>>(`/formulas/${id}/bom${params}`);
}

export async function getFormulaCost(id: string, currency?: string) {
  const params = currency ? `?currency=${currency}` : '';
  return request<ApiResponse<{ cost: number; currency: string }>>(`/formulas/${id}/cost${params}`);
}

export async function updateFormulaIngredients(id: string, data: { ingredients: any[] }) {
  return request<{ message: string }>(`/formulas/${id}/ingredients`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function approveFormula(id: string) {
  return request<ApiResponse<any>>(`/formulas/${id}/approve`, { method: 'POST' });
}

export async function createFormulaVersion(id: string) {
  return request<ApiResponse<any>>(`/formulas/${id}/version`, { method: 'POST' });
}

export async function deleteFormula(id: string) {
  return request<{ message: string }>(`/formulas/${id}`, { method: 'DELETE' });
}

// Raw Materials
export async function getRawMaterials(params?: PaginationParams & { stockType?: string; allergenCode?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.stockType) searchParams.set('stockType', params.stockType);
  if (params?.allergenCode) searchParams.set('allergenCode', params.allergenCode);
  return request<PaginatedResponse<any>>(`/raw-materials?${searchParams}`);
}

export async function getRawMaterial(id: string) {
  return request<ApiResponse<any>>(`/raw-materials/${id}`);
}

export async function createRawMaterial(data: any) {
  return request<ApiResponse<any>>('/raw-materials', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateRawMaterial(id: string, data: any) {
  return request<ApiResponse<any>>(`/raw-materials/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteRawMaterial(id: string) {
  return request<{ message: string }>(`/raw-materials/${id}`, { method: 'DELETE' });
}

// Inventory
export async function getLots(params?: PaginationParams & { rawMaterialId?: string; qcStatus?: string; search?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.rawMaterialId) searchParams.set('rawMaterialId', params.rawMaterialId);
  if (params?.qcStatus) searchParams.set('qcStatus', params.qcStatus);
  if (params?.search) searchParams.set('search', params.search);
  return request<PaginatedResponse<any>>(`/inventory/lots?${searchParams}`);
}

export async function createLot(data: any) {
  return request<ApiResponse<any>>('/inventory/lots', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getLot(id: string) {
  return request<ApiResponse<any>>(`/inventory/lots/${id}`);
}

export async function updateLotQcStatus(id: string, status: string, notes?: string) {
  return request<ApiResponse<any>>(`/inventory/lots/${id}/qc-status`, {
    method: 'PUT',
    body: JSON.stringify({ qcStatus: status, qcNotes: notes }),
  });
}

export async function getStockBalances(params?: { warehouseId?: string; rawMaterialId?: string; belowMin?: boolean; limit?: number }) {
  const searchParams = new URLSearchParams();
  if (params?.warehouseId) searchParams.set('warehouseId', params.warehouseId);
  if (params?.rawMaterialId) searchParams.set('rawMaterialId', params.rawMaterialId);
  if (params?.belowMin) searchParams.set('belowMin', 'true');
  if (params?.limit) searchParams.set('limit', String(params.limit));
  const query = searchParams.toString();
  return request<{ data: any[] }>(`/inventory/balances${query ? '?' + query : ''}`);
}

export async function getWarehouses() {
  return request<{ data: any[] }>('/inventory/warehouses');
}

export async function createWarehouse(data: { code: string; name: string; location?: string }) {
  return request<ApiResponse<any>>('/inventory/warehouses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWarehouse(id: string, data: { code?: string; name?: string; location?: string; isActive?: boolean }) {
  return request<ApiResponse<any>>(`/inventory/warehouses/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWarehouse(id: string) {
  return request<{ success: boolean }>(`/inventory/warehouses/${id}`, { method: 'DELETE' });
}

// Production
export async function getProductionOrders(params?: PaginationParams & { status?: string; search?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.search) searchParams.set('search', params.search);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/production/orders?${searchParams}`);
}

export async function createProductionOrder(data: any) {
  return request<ApiResponse<any>>('/production/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getProductionOrder(id: string) {
  return request<ApiResponse<any>>(`/production/orders/${id}`);
}

export async function updateProductionOrderStatus(id: string, status: string) {
  return request<ApiResponse<any>>(`/production/orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function deleteProductionOrder(id: string) {
  return request<{ message: string }>(`/production/orders/${id}`, { method: 'DELETE' });
}

export async function getBatches(params?: PaginationParams & { status?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  return request<PaginatedResponse<any>>(`/production/batches?${searchParams}`);
}

export async function getBatch(id: string) {
  return request<{ data: any }>(`/production/batches/${id}`);
}

// Sales
export async function getCustomers(params?: PaginationParams & { type?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.type) searchParams.set('type', params.type);
  return request<PaginatedResponse<any>>(`/sales/customers?${searchParams}`);
}

export async function createCustomer(data: any) {
  return request<ApiResponse<any>>('/sales/customers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCustomer(id: string) {
  return request<ApiResponse<any>>(`/sales/customers/${id}`);
}

export async function updateCustomer(id: string, data: any) {
  return request<ApiResponse<any>>(`/sales/customers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCustomer(id: string) {
  return request<{ message: string }>(`/sales/customers/${id}`, { method: 'DELETE' });
}

// Quotes
export async function getQuotes(params?: PaginationParams & { status?: string; customerId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  return request<PaginatedResponse<any>>(`/sales/quotes?${searchParams}`);
}

export async function getQuote(id: string) {
  return request<ApiResponse<any>>(`/sales/quotes/${id}`);
}

export async function createQuote(data: any) {
  return request<ApiResponse<any>>('/sales/quotes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateQuoteStatus(id: string, status: string) {
  return request<ApiResponse<any>>(`/sales/quotes/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

// Orders
export async function getOrders(params?: PaginationParams & { status?: string; customerId?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.status) searchParams.set('status', params.status);
  if (params?.customerId) searchParams.set('customerId', params.customerId);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/sales/orders?${searchParams}`);
}

export async function getOrder(id: string) {
  return request<ApiResponse<any>>(`/sales/orders/${id}`);
}

export async function createOrder(data: any) {
  return request<ApiResponse<any>>('/sales/orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateOrderStatus(id: string, status: string) {
  return request<ApiResponse<any>>(`/sales/orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

// Suppliers
export async function getSuppliers(params?: PaginationParams) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  return request<PaginatedResponse<any>>(`/suppliers?${searchParams}`);
}

export async function getSupplier(id: string) {
  return request<ApiResponse<any>>(`/suppliers/${id}`);
}

export async function createSupplier(data: any) {
  return request<ApiResponse<any>>('/suppliers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSupplier(id: string, data: any) {
  return request<ApiResponse<any>>(`/suppliers/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSupplier(id: string) {
  return request<{ message: string }>(`/suppliers/${id}`, { method: 'DELETE' });
}

// Purchase Orders
export async function getPurchaseOrders(params?: PaginationParams & { status?: string; supplierId?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.status) searchParams.set('status', params.status);
  if (params?.supplierId) searchParams.set('supplierId', params.supplierId);
  return request<PaginatedResponse<any>>(`/purchase-orders?${searchParams}`);
}

export async function getPurchaseOrder(id: string) {
  return request<ApiResponse<any>>(`/purchase-orders/${id}`);
}

export async function createPurchaseOrder(data: any) {
  return request<ApiResponse<any>>('/purchase-orders', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updatePurchaseOrder(id: string, data: any) {
  return request<ApiResponse<any>>(`/purchase-orders/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updatePurchaseOrderStatus(id: string, status: string) {
  return request<ApiResponse<any>>(`/purchase-orders/${id}/status`, {
    method: 'PUT',
    body: JSON.stringify({ status }),
  });
}

export async function deletePurchaseOrder(id: string) {
  return request<{ message: string }>(`/purchase-orders/${id}`, { method: 'DELETE' });
}

// Users Management
export async function getUsers(params?: PaginationParams & { role?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.search) searchParams.set('search', params.search);
  if (params?.role) searchParams.set('role', params.role);
  return request<PaginatedResponse<any>>(`/users?${searchParams}`);
}

export async function getUser(id: string) {
  return request<ApiResponse<any>>(`/users/${id}`);
}

export async function createUser(data: { email: string; name: string; password: string; role?: string; department?: string }) {
  return request<ApiResponse<any>>('/users', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateUser(id: string, data: { name?: string; email?: string; role?: string; department?: string; isActive?: boolean; password?: string }) {
  return request<ApiResponse<any>>(`/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteUser(id: string) {
  return request<{ success: boolean }>(`/users/${id}`, { method: 'DELETE' });
}

// Activity Logs
export async function getActivityLogs(params?: PaginationParams & { userId?: string; entityType?: string; action?: string; startDate?: string; endDate?: string }) {
  const searchParams = new URLSearchParams();
  if (params?.page) searchParams.set('page', params.page.toString());
  if (params?.pageSize) searchParams.set('pageSize', params.pageSize.toString());
  if (params?.userId) searchParams.set('userId', params.userId);
  if (params?.entityType) searchParams.set('entityType', params.entityType);
  if (params?.action) searchParams.set('action', params.action);
  if (params?.startDate) searchParams.set('startDate', params.startDate);
  if (params?.endDate) searchParams.set('endDate', params.endDate);
  return request<PaginatedResponse<any>>(`/activity?${searchParams}`);
}

export async function getActivityStats() {
  return request<ApiResponse<any>>('/activity/stats');
}

// User Activity & Time Tracking
export async function getUsersActivity(days: number = 30) {
  return request<ApiResponse<{
    usersWithActivity: Array<{
      userId: string;
      name: string;
      email: string;
      role: string;
      department: string | null;
      isActive: boolean;
      lastLoginAt: string | null;
      actionsCount: number;
      lastActivityAt: string;
      firstActivityAt: string;
      loginCount: number;
      createCount: number;
      updateCount: number;
      deleteCount: number;
      viewCount: number;
    }>;
    inactiveUsers: Array<{
      userId: string;
      name: string;
      email: string;
      role: string;
      department: string | null;
      isActive: boolean;
      lastLoginAt: string | null;
      actionsCount: number;
    }>;
    period: { days: number; startDate: string };
    totalActiveUsers: number;
    totalInactiveUsers: number;
  }>>(`/activity/users?days=${days}`);
}

export async function getUserActivity(userId: string, days: number = 30) {
  return request<ApiResponse<{
    user: {
      id: string;
      name: string;
      email: string;
      role: string;
      department: string | null;
      isActive: boolean;
      lastLoginAt: string | null;
      accountCreatedAt: string;
    };
    period: { days: number; startDate: string };
    summary: {
      totalActions: number;
      estimatedTimeMinutes: number;
      estimatedTimeFormatted: string;
      actionBreakdown: Array<{ action: string; count: number }>;
      topEntities: Array<{ entityType: string; count: number }>;
    };
    dailyActivity: Array<{ date: string; count: number }>;
    hourlyPattern: Array<{ hour: number; count: number }>;
    recentActivity: Array<{
      id: string;
      action: string;
      entityType: string;
      entityId: string | null;
      entityName: string | null;
      createdAt: string;
    }>;
  }>>(`/activity/users/${userId}?days=${days}`);
}

export async function getActivityDashboard(period: string = '7d') {
  return request<ApiResponse<{
    period: string;
    overview: {
      onlineNow: number;
      todayActiveUsers: number;
      periodActiveUsers: number;
    };
    activityTrend: Array<{ date: string; count: number; uniqueUsers: number }>;
    topUsers: Array<{ userId: string; name: string; email: string; actionsCount: number }>;
    topModules: Array<{ module: string; count: number }>;
    hourlyDistribution: Array<{ hour: number; count: number }>;
  }>>(`/activity/dashboard?period=${period}`);
}

export async function getOnlineUsers() {
  return request<ApiResponse<{
    onlineNow: Array<{
      userId: string;
      name: string;
      email: string;
      role: string;
      department: string | null;
      lastActivity: string;
      currentPage: string | null;
    }>;
    counts: {
      now: number;
      lastHour: number;
      last24Hours: number;
      last7Days: number;
      last30Days: number;
    };
    timestamp: string;
  }>>('/activity/online');
}

export async function logHeartbeat(page: string) {
  return request<{ success: boolean }>('/activity/heartbeat', {
    method: 'POST',
    body: JSON.stringify({ page }),
  });
}

// Traceability
export async function getLotTraceability(lotId: string) {
  return request<ApiResponse<any>>(`/inventory/lots/${lotId}/traceability`);
}

export async function getBatchTraceability(batchId: string) {
  return request<ApiResponse<any>>(`/production/batches/${batchId}/traceability`);
}

export async function recordBatchLotUsage(batchId: string, lotUsages: Array<{
  lotId: string;
  rawMaterialId: string;
  quantityUsed: number;
  unit?: string;
}>) {
  return request<ApiResponse<any>>(`/production/batches/${batchId}/lot-usage`, {
    method: 'POST',
    body: JSON.stringify({ lotUsages }),
  });
}

export async function createBatchWithTraceability(orderId: string, data: {
  quantity: number;
  unit?: string;
  manufacturingDate?: string;
  expiryDate?: string;
  notes?: string;
}) {
  return request<ApiResponse<any>>(`/production/orders/${orderId}/batches-with-traceability`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Enhanced Traceability API
export async function searchTraceability(query: string) {
  return request<ApiResponse<{ lots: any[]; batches: any[] }>>(`/traceability/search?q=${encodeURIComponent(query)}`);
}

export async function getLotForwardTrace(lotId: string) {
  return request<ApiResponse<any>>(`/traceability/lot/${lotId}`);
}

export async function getBatchBackwardTrace(batchId: string) {
  return request<ApiResponse<any>>(`/traceability/batch/${batchId}`);
}

export async function getRecallImpact(lotId: string) {
  return request<ApiResponse<any>>(`/traceability/recall-impact/${lotId}`);
}

// Contexa Integration
export async function getContextaJobs(productionOrderId: string) {
  return request<{ data: ContextaJob[] }>(`/contexa/orders/${productionOrderId}/jobs`);
}

export async function createContextaJob(data: {
  productionOrderId: string;
  system: 'COLIBRI' | 'CP_START' | 'TEX';
  targetQuantity?: number;
}) {
  return request<ApiResponse<ContextaJob>>('/contexa/jobs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getContextaJobWithContent(jobId: string) {
  return request<ApiResponse<ContextaJobWithContent>>(`/contexa/jobs/${jobId}`);
}

export async function updateContextaJobStatus(jobId: string, data: {
  status: 'pending' | 'sent' | 'in_progress' | 'completed' | 'failed' | 'cancelled';
  actualQuantity?: number;
  errorMessage?: string;
}) {
  return request<ApiResponse<ContextaJob>>(`/contexa/jobs/${jobId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function cancelContextaJob(jobId: string) {
  return request<{ message: string }>(`/contexa/jobs/${jobId}`, {
    method: 'DELETE',
  });
}

export async function getPendingContextaJobs(system?: 'COLIBRI' | 'CP_START' | 'TEX') {
  const params = system ? `?system=${system}` : '';
  return request<{ data: ContextaJob[] }>(`/contexa/jobs/pending${params}`);
}

// Quality Control
export async function getQcTestTypes() {
  return request<{ data: any }>('/quality/test-types');
}

export async function getQcQueue(type?: 'lot' | 'batch') {
  const params = type ? `?type=${type}` : '';
  return request<ApiResponse<any>>(`/quality/queue${params}`);
}

export async function getQcStats() {
  return request<ApiResponse<any>>('/quality/stats');
}

export async function updateLotQcStatusDetailed(lotId: string, data: { qcStatus: string; notes?: string; testResults?: any }) {
  return request<ApiResponse<any>>(`/quality/lots/${lotId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function updateBatchQcStatus(batchId: string, data: { qcStatus: string; status?: string; notes?: string }) {
  return request<ApiResponse<any>>(`/quality/batches/${batchId}/status`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function bulkApproveLots(lotIds: string[], notes?: string) {
  return request<{ message: string; data: { approvedCount: number } }>('/quality/lots/bulk-approve', {
    method: 'POST',
    body: JSON.stringify({ lotIds, notes }),
  });
}

export async function bulkApproveBatches(batchIds: string[], notes?: string) {
  return request<{ message: string; data: { approvedCount: number } }>('/quality/batches/bulk-approve', {
    method: 'POST',
    body: JSON.stringify({ batchIds, notes }),
  });
}

export async function getQuarantinedItems() {
  return request<ApiResponse<any>>('/quality/quarantine');
}

// Costing
export async function calculateFormulaCost(formulaId: string, options?: {
  currency?: string;
  includeLabor?: boolean;
  includeOverhead?: boolean;
  laborHours?: number;
  overheadPercentage?: number;
  save?: boolean;
}) {
  return request<ApiResponse<any>>(`/costing/formulas/${formulaId}/calculate`, {
    method: 'POST',
    body: JSON.stringify(options || {}),
  });
}

export async function getFormulaCostDetails(formulaId: string) {
  return request<ApiResponse<any>>(`/costing/formulas/${formulaId}/cost`);
}

export async function getFormulaCostHistory(formulaId: string) {
  return request<{ data: any[] }>(`/costing/formulas/${formulaId}/cost-history`);
}

export async function getMaterialCostHistory(materialId: string) {
  return request<{ data: any[] }>(`/costing/materials/${materialId}/cost-history`);
}

export async function addMaterialCost(materialId: string, data: {
  cost: number;
  currency?: string;
  effectiveDate?: string;
  source?: string;
  notes?: string;
  updateMaterial?: boolean;
}) {
  return request<ApiResponse<any>>(`/costing/materials/${materialId}/cost`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getExchangeRates() {
  return request<{ data: any[]; currencies: string[] }>('/costing/exchange-rates');
}

export async function convertCurrency(amount: number, fromCurrency: string, toCurrency: string) {
  return request<ApiResponse<any>>('/costing/convert', {
    method: 'POST',
    body: JSON.stringify({ amount, fromCurrency, toCurrency }),
  });
}

export async function getLaborRates() {
  return request<{ data: any[] }>('/costing/labor-rates');
}

export async function getCostCenters() {
  return request<{ data: any[] }>('/costing/cost-centers');
}

export async function createCostCenter(data: { code: string; name: string; type?: string; parentId?: string; allocationMethod?: string; allocationRate?: number }) {
  return request<ApiResponse<{ id: string }>>('/costing/cost-centers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createLaborRate(data: { operationType: string; hourlyRate: number; currency?: string; costCenterId?: string; effectiveFrom?: string; effectiveTo?: string; notes?: string }) {
  return request<ApiResponse<{ id: string }>>('/costing/labor-rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function addExchangeRate(data: { fromCurrency: string; toCurrency: string; rate: number; effectiveDate?: string; source?: string }) {
  return request<ApiResponse<{ id: string }>>('/costing/exchange-rates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTCMBRates() {
  return request<{ data: { rates: Record<string, number>; date: string }; source: string; note: string }>('/costing/tcmb-rates');
}

export async function updateExchangeRatesFromTCMB() {
  return request<{ data: { updated: number; date: string }; message: string }>('/costing/exchange-rates/update-from-tcmb', {
    method: 'POST',
  });
}
